import axios from 'axios';
import { getAuthHeaders, getUser } from './auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Track contact download and trigger notifications
 * @param {string} contactOwnerId - ID of the contact owner
 * @param {string} downloadSource - Source of download ('profile', 'group', 'browse', 'nearby')
 * @param {string} groupId - Optional group ID if download is from a group
 * @param {object} visitorInfo - Optional info for non-logged-in users {name, phone}
 */
export const trackContactDownload = async (contactOwnerId, downloadSource, groupId = null, visitorInfo = null) => {
  try {
    const headers = getAuthHeaders();
    const user = getUser();
    
    const trackData = {
      contactOwnerId,
      downloadSource,
      groupId,
      visitorUserId: user?.id || null,
      visitorName: visitorInfo?.name || null,
      visitorPhone: visitorInfo?.phone || null
    };
    
    await axios.post(
      `${BACKEND_URL}/api/contacts/track-download`,
      trackData,
      { headers }
    );
  } catch (error) {
    console.error('Failed to track contact download:', error);
    // Don't throw error - tracking failure shouldn't block download
  }
};

/**
 * Download vCard for a user
 * @param {string} userId - ID of the user whose vCard to download
 * @param {string} downloadSource - Source of download
 * @param {string} groupId - Optional group ID
 * @param {object} visitorInfo - Optional visitor info for non-logged-in users
 */
export const downloadUserVCard = async (userId, downloadSource, groupId = null, visitorInfo = null) => {
  try {
    // Track the download first
    await trackContactDownload(userId, downloadSource, groupId, visitorInfo);
    
    // Then download the vCard - get as TEXT not blob
    const response = await axios.get(
      `${BACKEND_URL}/api/users/${userId}/vcard`,
      { responseType: 'text' }
    );
    
    console.log('vCard content:', response.data); // Debug log
    
    // Create blob from text with proper MIME type
    const blob = new Blob([response.data], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contact_${userId}.vcf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to download vCard:', error);
    throw error;
  }
};

/**
 * Download vCard for a contact from a group
 * @param {string} groupId - ID of the contact group
 * @param {number} contactIndex - Index of the contact in the group
 * @param {string} contactOwnerId - ID of the group owner
 * @param {object} visitorInfo - Optional visitor info for non-logged-in users
 */
export const downloadGroupContactVCard = async (groupId, contactIndex, contactOwnerId, visitorInfo = null) => {
  try {
    // Track the download first
    await trackContactDownload(contactOwnerId, 'group', groupId, visitorInfo);
    
    // Then download the vCard - get as TEXT
    const response = await axios.get(
      `${BACKEND_URL}/api/contact-groups/${groupId}/contacts/${contactIndex}/vcard`,
      { responseType: 'text' }
    );
    
    console.log('vCard content:', response.data); // Debug log
    
    // Create blob from text with proper MIME type
    const blob = new Blob([response.data], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contact_${contactIndex}.vcf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to download group contact vCard:', error);
    throw error;
  }
};

/**
 * Save contact to backend database (persistent storage)
 * @param {object} contactData - Contact information to save
 * @param {string} source - Source of the contact ('card_scanner', 'browse', 'nearby')
 * @returns {object} Response with success status and contactId
 */
export const saveContactToBackend = async (contactData, source = 'card_scanner') => {
  try {
    const headers = getAuthHeaders();
    const user = getUser();
    
    if (!user || !headers.Authorization) {
      console.warn('User not logged in, skipping backend save');
      return { success: false, message: 'User not logged in' };
    }
    
    // Prepare contact data for backend
    const backendContact = {
      name: contactData.name || contactData.contactName || '',
      businessName: contactData.businessName || contactData.company || '',
      title: contactData.title || contactData.jobTitle || '',
      email: contactData.email || '',
      phone: contactData.phone || contactData.mobile || '',
      mobile: contactData.mobile || contactData.phone || '',
      website: contactData.website || '',
      address: contactData.address || '',
      linkedin: contactData.linkedin || '',
      notes: contactData.notes || `Saved from ${source}`,
    };
    
    const response = await axios.post(
      `${BACKEND_URL}/api/contacts/save`,
      backendContact,
      { headers }
    );
    
    console.log('✅ Contact saved to backend:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to save contact to backend:', error);
    
    if (error.response?.status === 401) {
      toast.error('Please login to save contacts permanently');
    }
    
    throw error;
  }
};

/**
 * Fetch all contacts from backend database
 * @returns {array} List of contacts
 */
export const fetchContactsFromBackend = async () => {
  try {
    const headers = getAuthHeaders();
    const user = getUser();
    
    if (!user || !headers.Authorization) {
      console.warn('User not logged in, cannot fetch backend contacts');
      return [];
    }
    
    const response = await axios.get(
      `${BACKEND_URL}/api/contacts/list`,
      { headers }
    );
    
    console.log('✅ Fetched contacts from backend:', response.data.count);
    return response.data.contacts || [];
  } catch (error) {
    console.error('❌ Failed to fetch contacts from backend:', error);
    return [];
  }
};

/**
 * Migrate localStorage contacts to backend database (one-time migration)
 * @returns {object} Migration result with counts
 */
export const migrateLocalStorageContacts = async () => {
  try {
    const headers = getAuthHeaders();
    const user = getUser();
    
    if (!user || !headers.Authorization) {
      return { success: false, message: 'User not logged in' };
    }
    
    // Get contacts from localStorage
    const localContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
    
    if (localContacts.length === 0) {
      return { success: true, migrated: 0, message: 'No contacts to migrate' };
    }
    
    let migrated = 0;
    let failed = 0;
    
    for (const contact of localContacts) {
      try {
        await saveContactToBackend(contact, 'migration');
        migrated++;
      } catch (error) {
        console.error('Failed to migrate contact:', contact.name, error);
        failed++;
      }
    }
    
    console.log(`✅ Migration complete: ${migrated} contacts saved, ${failed} failed`);
    
    // Mark migration as complete
    localStorage.setItem('contactsMigrated', 'true');
    
    return {
      success: true,
      migrated,
      failed,
      message: `Successfully migrated ${migrated} contacts`
    };
  } catch (error) {
    console.error('Migration error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
